/**
 * PortfolioScoreboard — Top-of-dashboard aggregate strip.
 * Shows total portfolio revenue, combined pacing, traffic-light counts,
 * and estimated month-end across ALL portcos.
 */

import { PortcoMetrics } from "../utils/PowerBIApiCall";
import { useAnimatedNumber } from "../hooks/useAnimatedNumber";
import { CalcTooltip } from "./CalcTooltip";
import {
  formatCurrency as formatCurrencyFull,
  formatCurrencyCompact,
} from "../utils/format";
import { SERIES } from "../utils/chartTheme";
import { buildForecast } from "../utils/forecastModel";

interface PortfolioScoreboardProps {
  metrics: Record<string, PortcoMetrics>;
  animate?: boolean;
}

/** Abbreviate only ≥$1M; everything smaller stays full precision. */
function formatCurrency(value: number): string {
  return Math.abs(value) >= 1_000_000
    ? formatCurrencyCompact(value)
    : formatCurrencyFull(value);
}

interface TrafficCounts {
  onTrack: number;
  atRisk: number;
  offTrack: number;
}

function computeAggregates(metrics: Record<string, PortcoMetrics>) {
  let totalMtdActual = 0;
  let totalMtdBudget = 0;
  let totalYtdActual = 0;
  let totalYtdBudget = 0;
  let totalEstimatedMonthEnd = 0;
  let totalMonthlyBudget = 0;
  const traffic: TrafficCounts = { onTrack: 0, atRisk: 0, offTrack: 0 };

  Object.values(metrics).forEach((m) => {
    totalMtdActual += m.mtd.actual;
    totalMtdBudget += m.mtd.budgetProrated;
    totalYtdActual += m.ytd.actual;
    totalYtdBudget += m.ytd.budgetProrated;

    totalMonthlyBudget += m.mtd.budgetFull;

    // Use MTD pacing for traffic light
    const pace =
      m.mtd.budgetProrated > 0
        ? (m.mtd.actual / m.mtd.budgetProrated) * 100
        : 100;
    if (pace >= 100) {
      traffic.onTrack++;
    } else if (pace >= 90) {
      traffic.atRisk++;
    } else {
      traffic.offTrack++;
    }

    // Month-end estimate from the canonical model when a daily trend is
    // available, else a flat month-to-date rate. Open orders excluded either
    // way — the run rate already covers that pipeline.
    const fcst =
      m.dailyTrend && m.dailyTrend.length > 2
        ? buildForecast(m.dailyTrend, m.mtd, m.mtd.budgetFull)
        : null;
    if (fcst) {
      totalEstimatedMonthEnd += fcst.summary.forecastTotal;
    } else {
      const dailyRate =
        m.mtd.businessDaysElapsed > 0
          ? m.mtd.actual / m.mtd.businessDaysElapsed
          : 0;
      totalEstimatedMonthEnd +=
        m.mtd.actual +
        dailyRate * (m.mtd.businessDaysTotal - m.mtd.businessDaysElapsed);
    }
  });

  const mtdPacingPct =
    totalMtdBudget > 0 ? (totalMtdActual / totalMtdBudget) * 100 : 0;

  const ytdPacingPct =
    totalYtdBudget > 0 ? (totalYtdActual / totalYtdBudget) * 100 : 0;

  const estimatedVsBudgetPct =
    totalMonthlyBudget > 0
      ? (totalEstimatedMonthEnd / totalMonthlyBudget) * 100
      : 0;

  return {
    totalMtdActual,
    totalMtdBudget,
    totalYtdActual,
    totalYtdBudget,
    mtdPacingPct,
    ytdPacingPct,
    totalEstimatedMonthEnd,
    estimatedVsBudgetPct,
    totalMonthlyBudget,
    traffic,
    portcoCount: Object.keys(metrics).length,
  };
}

/** Dashboard pacing thresholds (>=100 / >=90) — intentionally different from FinancialTable's. */
function pacingColor(pct: number): string {
  if (pct >= 100) return SERIES.positive;
  if (pct >= 90) return SERIES.warning;
  return SERIES.negative;
}

function AnimatedCurrency({
  value,
  animate,
}: {
  value: number;
  animate: boolean;
}) {
  const displayed = useAnimatedNumber(value, animate ? 1200 : 0);
  return <>{formatCurrency(displayed)}</>;
}

function AnimatedPct({ value, animate }: { value: number; animate: boolean }) {
  const displayed = useAnimatedNumber(value, animate ? 1200 : 0);
  return <>{displayed.toFixed(1)}%</>;
}

export function PortfolioScoreboard({
  metrics,
  animate = true,
}: PortfolioScoreboardProps) {
  const agg = computeAggregates(metrics);
  if (agg.portcoCount === 0) return null;

  const mtdColor = pacingColor(agg.mtdPacingPct);
  const estimateColor = pacingColor(agg.estimatedVsBudgetPct);

  return (
    <div className="bg-white rounded-xl shadow-card border border-gray-200/70 overflow-hidden mb-6">
      <div className="px-6 py-3.5 border-b border-gray-100 flex items-center justify-between">
        <h2 className="text-[15px] font-semibold text-gray-900">
          Portfolio Overview
        </h2>
        <span className="text-xs text-gray-400">
          {agg.portcoCount} companies
        </span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-0 divide-x divide-gray-100">
        {/* Total MTD Revenue */}
        <div className="p-5 text-center">
          <p className="microlabel mb-1">Total MTD Revenue</p>
          <p className="text-xl font-bold tracking-tight tabular-nums text-gray-900">
            <AnimatedCurrency value={agg.totalMtdActual} animate={animate} />
          </p>
          <CalcTooltip
            formula="Total MTD Actual ÷ Total MTD Budget (Prorated)"
            values={`= ${formatCurrency(agg.totalMtdActual)} ÷ ${formatCurrency(agg.totalMtdBudget)} = ${agg.mtdPacingPct.toFixed(1)}%`}
          >
            <p
              className="text-xs font-semibold tabular-nums mt-1"
              style={{ color: mtdColor }}
            >
              <AnimatedPct value={agg.mtdPacingPct} animate={animate} /> of
              budget
            </p>
          </CalcTooltip>
        </div>

        {/* Forecasted Month-End */}
        <div className="p-5 text-center">
          <p className="microlabel mb-1">Est. Month-End</p>
          <div>
            <CalcTooltip
              formula="Sum of each company's month-end forecast (open orders excluded)"
              values={`= ${formatCurrency(agg.totalEstimatedMonthEnd)}`}
            >
              <span className="text-xl font-bold tracking-tight tabular-nums text-gray-900">
                <AnimatedCurrency
                  value={agg.totalEstimatedMonthEnd}
                  animate={animate}
                />
              </span>
            </CalcTooltip>
          </div>
          <CalcTooltip
            formula="Est. Month-End ÷ Total Monthly Budget (Full)"
            values={`= ${formatCurrency(agg.totalEstimatedMonthEnd)} ÷ ${formatCurrency(agg.totalMonthlyBudget)} = ${agg.estimatedVsBudgetPct.toFixed(1)}%`}
          >
            <p
              className="text-xs font-semibold tabular-nums mt-1"
              style={{ color: estimateColor }}
            >
              <AnimatedPct value={agg.estimatedVsBudgetPct} animate={animate} />{" "}
              of budget
            </p>
          </CalcTooltip>
        </div>

        {/* Portfolio Health */}
        <div className="p-5 text-center">
          <p className="microlabel mb-1">Portfolio Health</p>
          <div className="flex items-center justify-center gap-3 mt-2">
            {agg.traffic.onTrack > 0 && (
              <div className="flex items-center gap-1.5">
                <span className="w-3 h-3 rounded-full bg-emerald-500 inline-block" />
                <span className="text-lg font-bold text-emerald-700">
                  {agg.traffic.onTrack}
                </span>
              </div>
            )}
            {agg.traffic.atRisk > 0 && (
              <div className="flex items-center gap-1.5">
                <span className="w-3 h-3 rounded-full bg-amber-500 inline-block" />
                <span className="text-lg font-bold text-amber-700">
                  {agg.traffic.atRisk}
                </span>
              </div>
            )}
            {agg.traffic.offTrack > 0 && (
              <div className="flex items-center gap-1.5">
                <span className="w-3 h-3 rounded-full bg-red-500 inline-block" />
                <span className="text-lg font-bold text-red-700">
                  {agg.traffic.offTrack}
                </span>
              </div>
            )}
          </div>
          <p className="text-[10px] text-gray-400 mt-1">
            On Track / At Risk / Off Track
          </p>
        </div>

        {/* YTD Pacing */}
        <div className="p-5 text-center">
          <p className="microlabel mb-1">YTD Pacing</p>
          <CalcTooltip
            formula="Total YTD Actual ÷ Total YTD Budget (Prorated)"
            values={`= ${formatCurrency(agg.totalYtdActual)} ÷ ${formatCurrency(agg.totalYtdBudget)} = ${agg.ytdPacingPct.toFixed(1)}%`}
          >
            <p
              className="text-xl font-bold tracking-tight tabular-nums"
              style={{ color: pacingColor(agg.ytdPacingPct) }}
            >
              <AnimatedPct value={agg.ytdPacingPct} animate={animate} />
            </p>
          </CalcTooltip>
          <p className="text-[10px] text-gray-400 mt-1">vs prorated budget</p>
        </div>
      </div>
    </div>
  );
}
